import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import useSeo from "@/lib/useSeo";
import { useFaqs } from "@/lib/faqs";
import { useT } from "@/lib/i18n";
import { SafeJsonLd } from "@/lib/safeHtml";

const PreguntasFrecuentes = () => {
  const t = useT();
  const { categories } = useFaqs();
  useSeo({
    title: {
      es: "Preguntas Frecuentes | Método TAO",
      ca: "Preguntes Freqüents | Mètode TAO",
    },
    description: {
      es: "Todas las dudas habituales sobre el Método TAO: yoga oncológico durante la quimioterapia, fibromialgia, fatiga crónica, sesiones online desde España y Latinoamérica.",
      ca: "Tots els dubtes habituals sobre el Mètode TAO: ioga oncològic durant la quimioteràpia, fibromiàlgia, fatiga crònica, sessions online des d'Espanya i Llatinoamèrica.",
    },
    path: "/preguntas-frecuentes",
  });

  const [openKey, setOpenKey] = useState("oncologia-0");

  const allItems = categories.flatMap((c) => c.items);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allItems.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };

  return (
    <div className="bg-ivory">
      <SafeJsonLd data={jsonLd} />

      <section className="pt-12 md:pt-20 pb-12 md:pb-16" data-testid="page-faq">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">
              {t("faq.eyebrow")}
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98] max-w-3xl">
              {t("faq.title1")} <span className="italic text-terracotta">{t("faq.title2")}</span>.
            </h1>
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-2xl">
              {t("faq.lead")}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <nav className="mt-10 flex flex-wrap gap-3" aria-label={t("faq.skipTo")}>
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={`#${cat.id}`}
                  data-testid={`faq-nav-${cat.id}`}
                  className="text-sm border border-charcoal/15 hover:border-terracotta hover:text-terracotta text-charcoal/75 rounded-full px-5 py-2.5 transition-colors"
                >
                  {cat.title}
                </a>
              ))}
            </nav>
          </Reveal>
        </div>
      </section>

      {categories.map((cat, catIdx) => (
        <section
          key={cat.id}
          id={cat.id}
          className={`py-16 md:py-20 scroll-mt-24 ${catIdx % 2 === 1 ? "bg-cream/40" : ""}`}
          data-testid={`faq-category-${cat.id}`}
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <Reveal className="lg:col-span-4">
              <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-5">
                {t("faq.category")} {catIdx + 1} / {categories.length}
              </p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal leading-[1.05] mb-5">
                {cat.title}
              </h2>
              <p className="text-charcoal/70 leading-relaxed font-light">
                {cat.description}
              </p>
              <Link
                to={`/${cat.id === "online" ? "sesiones-online" : cat.id}`}
                data-testid={`faq-deeplink-${cat.id}`}
                className="mt-7 inline-flex items-center gap-2 text-sm text-charcoal hover:text-terracotta border-b border-terracotta pb-1 transition-colors"
              >
                {t("faq.deepLink")} <ArrowRight size={14} />
              </Link>
            </Reveal>

            <Reveal delay={0.08} className="lg:col-span-8">
              <ul className="divide-y divide-sand">
                {cat.items.map((it, i) => {
                  const key = `${cat.id}-${i}`;
                  const isOpen = openKey === key;
                  return (
                    <li key={it.q} data-testid={`faq-item-${key}`}>
                      <button
                        type="button"
                        onClick={() => setOpenKey(isOpen ? "" : key)}
                        aria-expanded={isOpen}
                        data-testid={`faq-question-${key}`}
                        className="w-full text-left py-6 flex items-start gap-5 group"
                      >
                        <span className="font-serif text-xl md:text-2xl text-charcoal flex-1 leading-snug group-hover:text-terracotta transition-colors">
                          {it.q}
                        </span>
                        <span
                          className={`mt-2 w-9 h-9 rounded-full border border-charcoal/20 flex items-center justify-center text-charcoal/70 shrink-0 transition-transform ${isOpen ? "rotate-180 bg-terracotta/15 border-terracotta/40 text-terracotta" : ""}`}
                        >
                          <ChevronDown size={16} />
                        </span>
                      </button>
                      <div
                        className={`grid transition-[grid-template-rows] duration-300 ${isOpen ? "grid-rows-[1fr] pb-6" : "grid-rows-[0fr]"}`}
                      >
                        <div className="overflow-hidden">
                          <p
                            className="text-charcoal/75 leading-relaxed text-[16px] max-w-2xl"
                            data-testid={`faq-answer-${key}`}
                          >
                            {it.a}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
          </div>
        </section>
      ))}

      <SectionCTA
        title={t("faq.notFound.title")}
        body={t("faq.notFound.body")}
        primaryLabel={t("cta.startTalking.button")}
        primaryTo="/sesion-gratuita"
      />
    </div>
  );
};

export default PreguntasFrecuentes;
